import { useEffect, useMemo, useRef, useState } from "react";
import {
  buildTimeAxisTicks,
  buildFrequencyAxisTicks,
  clampNumber,
  formatFrequencyAxisLabel,
  formatWaveformAmplitudeLabel,
  getAudioContextConstructor,
  getCanvasContext,
  loadSpectrogramAnalysis,
  normalizeFrequencyPosition,
  normalizeWaveformAmplitude,
  paintSpectrogramHeatmap,
  paintSpectrogramPlaceholder,
  paintWaveformChart,
  paintWaveformPlaceholder,
  SPECTROGRAM_DEFAULT_MAX_HZ,
  SPECTROGRAM_MAX_ZOOM,
  SPECTROGRAM_MIN_ZOOM,
  SPECTROGRAM_TIME_TICK_COUNT,
  WAVEFORM_AXIS_TICKS_MV,
} from "./audioAnalysis";

export function useAudioVisualisation({
  mediaRef,
  mediaUrl,
  t,
  useLogFrequencyScale = false,
}) {
  const canvasRef = useRef(null);
  const waveformCanvasRef = useRef(null);
  const playheadRef = useRef(null);
  const waveformPlayheadRef = useRef(null);
  const [analysis, setAnalysis] = useState(null);
  const [analysisState, setAnalysisState] = useState("idle");
  const [timeline, setTimeline] = useState({
    currentTime: 0,
    duration: 0,
  });
  const [spectrogramDuration, setSpectrogramDuration] = useState(0);
  const [timeZoom, setTimeZoom] = useState(1);
  const [viewStartTime, setViewStartTime] = useState(0);
  const [maxFrequencyHz, setMaxFrequencyHz] = useState(
    SPECTROGRAM_DEFAULT_MAX_HZ,
  );

  const totalDuration = Math.max(
    Number.isFinite(timeline.duration) && timeline.duration > 0
      ? timeline.duration
      : 0,
    Number.isFinite(analysis?.duration) && analysis.duration > 0
      ? analysis.duration
      : 0,
    Number.isFinite(spectrogramDuration) && spectrogramDuration > 0
      ? spectrogramDuration
      : 0,
  );
  const viewportDuration =
    totalDuration > 0 ? totalDuration / Math.max(timeZoom, 1) : 0;
  const maxViewportStartTime = Math.max(totalDuration - viewportDuration, 0);
  const clampedViewStartTime = clampNumber(
    viewStartTime,
    0,
    maxViewportStartTime,
  );
  const viewportEndTime = Math.min(
    clampedViewStartTime + viewportDuration,
    totalDuration,
  );
  const viewportStartRatio =
    totalDuration > 0 ? clampedViewStartTime / totalDuration : 0;
  const viewportSpanRatio =
    totalDuration > 0 ? viewportDuration / totalDuration : 1;

  const timeLabels = useMemo(() => {
    const startTime = Number.isFinite(clampedViewStartTime)
      ? clampedViewStartTime
      : 0;
    const endTime = Number.isFinite(viewportEndTime) ? viewportEndTime : 0;

    return buildTimeAxisTicks(
      startTime,
      endTime,
      SPECTROGRAM_TIME_TICK_COUNT,
    );
  }, [clampedViewStartTime, viewportEndTime]);

  const frequencyTicks = useMemo(() => {
    return buildFrequencyAxisTicks(maxFrequencyHz, {
      logScale: useLogFrequencyScale,
    }).map((frequencyHz) => ({
      frequencyHz,
      label: formatFrequencyAxisLabel(frequencyHz),
      top: `${(
        1 -
        normalizeFrequencyPosition(frequencyHz, maxFrequencyHz, {
          logScale: useLogFrequencyScale,
        })
      ) * 100}%`,
    }));
  }, [maxFrequencyHz, useLogFrequencyScale]);

  const waveformTicks = useMemo(() => {
    const millivoltsLabel = t(
      "profilePage.recordingViewerWaveformUnitMillivolts",
      "mV",
    );

    return WAVEFORM_AXIS_TICKS_MV.map((amplitudeMv) => ({
      amplitudeMv,
      label: formatWaveformAmplitudeLabel(amplitudeMv, millivoltsLabel),
      top: `${normalizeWaveformAmplitude(amplitudeMv) * 100}%`,
    }));
  }, [t]);

  useEffect(() => {
    const leftPercent =
      viewportDuration > 0
        ? ((timeline.currentTime - clampedViewStartTime) / viewportDuration) *
          100
        : 0;
    const nextLeft = `${clampNumber(leftPercent, 0, 100)}%`;

    [playheadRef.current, waveformPlayheadRef.current].forEach(
      (playheadElement) => {
        if (playheadElement) {
          playheadElement.style.left = nextLeft;
        }
      },
    );
  }, [clampedViewStartTime, timeline.currentTime, viewportDuration]);

  useEffect(() => {
    const mediaElement = mediaRef.current;
    if (!mediaElement) {
      return undefined;
    }

    let destroyed = false;

    const syncTimeline = () => {
      if (destroyed) {
        return;
      }

      const nextDuration = Number.isFinite(mediaElement.duration)
        ? mediaElement.duration
        : 0;
      const nextCurrentTime = Number.isFinite(mediaElement.currentTime)
        ? mediaElement.currentTime
        : 0;

      setTimeline((previous) => {
        if (
          Math.abs(previous.currentTime - nextCurrentTime) < 0.05 &&
          Math.abs(previous.duration - nextDuration) < 0.05
        ) {
          return previous;
        }

        return {
          currentTime: nextCurrentTime,
          duration: nextDuration,
        };
      });
    };

    syncTimeline();
    mediaElement.addEventListener("loadedmetadata", syncTimeline);
    mediaElement.addEventListener("durationchange", syncTimeline);
    mediaElement.addEventListener("timeupdate", syncTimeline);
    mediaElement.addEventListener("seeked", syncTimeline);
    mediaElement.addEventListener("seeking", syncTimeline);
    mediaElement.addEventListener("play", syncTimeline);
    mediaElement.addEventListener("pause", syncTimeline);
    mediaElement.addEventListener("ended", syncTimeline);

    return () => {
      destroyed = true;
      mediaElement.removeEventListener("loadedmetadata", syncTimeline);
      mediaElement.removeEventListener("durationchange", syncTimeline);
      mediaElement.removeEventListener("timeupdate", syncTimeline);
      mediaElement.removeEventListener("seeked", syncTimeline);
      mediaElement.removeEventListener("seeking", syncTimeline);
      mediaElement.removeEventListener("play", syncTimeline);
      mediaElement.removeEventListener("pause", syncTimeline);
      mediaElement.removeEventListener("ended", syncTimeline);
    };
  }, [mediaRef, mediaUrl]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = getCanvasContext(canvas);
    const waveformCanvas = waveformCanvasRef.current;
    const waveformContext = getCanvasContext(waveformCanvas);

    if (!canvas || !context || !waveformCanvas || !waveformContext) {
      setAnalysisState("unavailable");
      return undefined;
    }

    paintSpectrogramPlaceholder(context, canvas.width, canvas.height);
    paintWaveformPlaceholder(
      waveformContext,
      waveformCanvas.width,
      waveformCanvas.height,
    );
    setAnalysis(null);
    setTimeZoom(1);
    setViewStartTime(0);

    if (!mediaUrl) {
      setAnalysisState("idle");
      setSpectrogramDuration(0);
      setMaxFrequencyHz(SPECTROGRAM_DEFAULT_MAX_HZ);
      return undefined;
    }

    if (!getAudioContextConstructor()) {
      setAnalysisState("unavailable");
      return undefined;
    }

    let cancelled = false;
    setAnalysisState("loading");

    loadSpectrogramAnalysis(mediaUrl)
      .then((nextAnalysis) => {
        if (cancelled) {
          return;
        }

        setAnalysis(nextAnalysis);
        setSpectrogramDuration(nextAnalysis.duration);
        setMaxFrequencyHz(nextAnalysis.maxFrequencyHz);
        setAnalysisState("ready");
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }

        paintSpectrogramPlaceholder(context, canvas.width, canvas.height);
        paintWaveformPlaceholder(
          waveformContext,
          waveformCanvas.width,
          waveformCanvas.height,
        );
        setAnalysis(null);
        setSpectrogramDuration(0);
        setMaxFrequencyHz(SPECTROGRAM_DEFAULT_MAX_HZ);
        setAnalysisState("unavailable");
      });

    return () => {
      cancelled = true;
    };
  }, [mediaUrl]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = getCanvasContext(canvas);
    const waveformCanvas = waveformCanvasRef.current;
    const waveformContext = getCanvasContext(waveformCanvas);

    if (
      !canvas ||
      !context ||
      !waveformCanvas ||
      !waveformContext ||
      !analysis?.columns?.length
    ) {
      return;
    }

    paintSpectrogramHeatmap(
      context,
      canvas.width,
      canvas.height,
      analysis.columns,
      {
        logScale: useLogFrequencyScale,
        maxFrequencyHz,
        sourceStartRatio: viewportStartRatio,
        sourceSpanRatio: viewportSpanRatio,
      },
    );
    paintWaveformChart(
      waveformContext,
      waveformCanvas.width,
      waveformCanvas.height,
      analysis.waveformColumns,
      {
        sourceStartRatio: viewportStartRatio,
        sourceSpanRatio: viewportSpanRatio,
      },
    );
  }, [
    analysis,
    maxFrequencyHz,
    useLogFrequencyScale,
    viewportSpanRatio,
    viewportStartRatio,
  ]);

  const handleZoomChange = (nextZoomLevel) => {
    const safeNextZoomLevel = clampNumber(
      nextZoomLevel,
      SPECTROGRAM_MIN_ZOOM,
      SPECTROGRAM_MAX_ZOOM,
    );
    const focusTime =
      Number.isFinite(timeline.currentTime) && timeline.currentTime > 0
        ? timeline.currentTime
        : clampedViewStartTime + viewportDuration / 2;
    const nextViewportDuration =
      totalDuration > 0 ? totalDuration / safeNextZoomLevel : 0;
    const nextMaxViewportStartTime = Math.max(
      totalDuration - nextViewportDuration,
      0,
    );

    setTimeZoom(safeNextZoomLevel);
    setViewStartTime(
      clampNumber(
        focusTime - nextViewportDuration / 2,
        0,
        nextMaxViewportStartTime,
      ),
    );
  };

  return {
    analysisState,
    canvasRef,
    clampedViewStartTime,
    frequencyTicks,
    handleZoomChange,
    maxViewportStartTime,
    playheadRef,
    timeLabels,
    timeZoom,
    totalDuration,
    waveformCanvasRef,
    waveformPlayheadRef,
    waveformTicks,
    windowSliderDisabled:
      timeZoom <= SPECTROGRAM_MIN_ZOOM || maxViewportStartTime <= 0,
    setViewStartTime,
  };
}
